import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ApiAuthError, fetchSessionDetail } from '../api/client';
import { displayUserId } from '../lib/userId';
import type { Outcome, PrimaryTask } from '../api/types';
import { useToken } from '../auth/TokenContext';

interface Props {
  sessionId: string | null;
  onClose: () => void;
  /**
   * Called when the user clicks the user_id pill in the meta grid. The
   * parent decides what happens — typically opens the user-detail
   * drawer stacked on top. Optional so old call sites keep working;
   * when absent, the user_id renders as plain text.
   */
  onSelectUser?: (userId: string) => void;
}

const TASK_LABELS: Record<PrimaryTask, string> = {
  page_migration: 'Page migration',
  refinement: 'Refinement',
  skill_authoring: 'Skill authoring',
  content_generation: 'Content generation',
  other: 'Other',
};

const OUTCOME_COLORS: Record<Outcome, string> = {
  completed: '#10b981',
  partial: '#f59e0b',
  exploratory: '#6366f1',
  stuck: '#ef4444',
  abandoned: '#71717a',
};

const RATIONALE_LABELS: Record<string, string> = {
  outcome: 'Outcome',
  primary_task: 'Primary task',
  engagement_depth: 'Engagement depth',
  session_feel: 'Session feel',
};

function formatDuration(min: number): string {
  if (min < 1) return '<1m';
  if (min < 60) return `${min}m`;
  const hours = Math.floor(min / 60);
  const remaining = min % 60;
  return remaining > 0 ? `${hours}h ${remaining}m` : `${hours}h`;
}

export function SessionDetailDrawer({
  sessionId,
  onClose,
  onSelectUser,
}: Props): JSX.Element | null {
  const { token } = useToken();
  const tokenKey = token?.slice(-8);

  // Close on Escape — drawer is modal-ish; users expect that.
  useEffect(() => {
    if (!sessionId) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [sessionId, onClose]);

  const detailQuery = useQuery({
    queryKey: ['session-detail', sessionId, tokenKey],
    queryFn: () => fetchSessionDetail(sessionId!, token),
    enabled: !!sessionId && !!token,
  });

  if (!sessionId) return null;

  const detail = detailQuery.data?.session;
  const isAuthError = detailQuery.error instanceof ApiAuthError;

  return (
    <>
      <div className="drawer-overlay" onClick={onClose} aria-hidden="true" />
      <aside
        className="drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Session detail"
      >
        <header className="drawer-header">
          <div>
            <h2>Session</h2>
            <code className="drawer-session-id">{sessionId}</code>
          </div>
          <button
            type="button"
            className="drawer-close"
            onClick={onClose}
            title="Close (Esc)"
          >
            ✕
          </button>
        </header>

        <div className="drawer-body">
          {detailQuery.isLoading && <p className="drawer-loading">Loading…</p>}
          {detailQuery.isError && !isAuthError && (
            <p className="drawer-error">Failed to load: {String(detailQuery.error)}</p>
          )}
          {detail && (
            <>
              <section className="drawer-section">
                <div className="drawer-meta-grid">
                  <div>
                    <div className="drawer-meta-label">User</div>
                    {detail.userId && onSelectUser ? (
                      <button
                        type="button"
                        className="drawer-meta-userlink"
                        onClick={() => onSelectUser(detail.userId!)}
                        title={detail.userId}
                      >
                        {displayUserId(detail.userId)}
                      </button>
                    ) : (
                      <code className="drawer-meta-value">{displayUserId(detail.userId)}</code>
                    )}
                  </div>
                  <div>
                    <div className="drawer-meta-label">Started</div>
                    <div className="drawer-meta-value">
                      {new Date(detail.startedAt).toLocaleString('en-US')}
                    </div>
                  </div>
                  <div>
                    <div className="drawer-meta-label">Duration</div>
                    <div className="drawer-meta-value">{formatDuration(detail.durationMin)}</div>
                  </div>
                  <div>
                    <div className="drawer-meta-label">Events</div>
                    <div className="drawer-meta-value">
                      {detail.eventCount.toLocaleString('en-US')}
                    </div>
                  </div>
                  <div>
                    <div className="drawer-meta-label">Models</div>
                    <div className="drawer-meta-value">
                      {detail.modelsUsed.length > 0 ? detail.modelsUsed.join(', ') : '—'}
                    </div>
                  </div>
                  <div>
                    <div className="drawer-meta-label">Harness</div>
                    <div className="drawer-meta-value">
                      {detail.harnessesUsed.length > 0 ? detail.harnessesUsed.join(', ') : '—'}
                    </div>
                  </div>
                  <div>
                    <div className="drawer-meta-label">SLICC</div>
                    <div className="drawer-meta-value">
                      {detail.sliccVersionsUsed.length > 0
                        ? detail.sliccVersionsUsed.join(', ')
                        : '—'}
                    </div>
                  </div>
                  <div>
                    <div className="drawer-meta-label">Classifier</div>
                    <div className="drawer-meta-value">{detail.classifierVersion ?? '—'}</div>
                  </div>
                </div>
              </section>

              <section className="drawer-section">
                <h3>Classification</h3>
                <div className="drawer-class-grid">
                  <span className="badge badge-task">
                    {detail.primaryTask ? TASK_LABELS[detail.primaryTask] : 'unclassified'}
                  </span>
                  {detail.outcome && (
                    <span
                      className="badge badge-outcome"
                      style={{
                        backgroundColor: `${OUTCOME_COLORS[detail.outcome]}22`,
                        color: OUTCOME_COLORS[detail.outcome],
                      }}
                    >
                      {detail.outcome}
                    </span>
                  )}
                  {detail.sessionFeel && (
                    <span className="badge badge-neutral">feel: {detail.sessionFeel}</span>
                  )}
                  {detail.engagementDepth && (
                    <span className="badge badge-neutral">{detail.engagementDepth}</span>
                  )}
                  {detail.safetyFlags.map((flag) => (
                    <span key={flag} className="badge badge-flag">
                      ⚑ {flag}
                    </span>
                  ))}
                </div>
              </section>

              {detail.journeySummary.length > 0 && (
                <section className="drawer-section">
                  <h3>Journey</h3>
                  <ul className="drawer-journey-summary">
                    {detail.journeySummary.map((bullet, i) => (
                      <li key={i}>{bullet}</li>
                    ))}
                  </ul>
                </section>
              )}

              {detail.journeyMilestones.length > 0 && (
                <section className="drawer-section">
                  <h3>Milestones</h3>
                  <ol className="drawer-milestones">
                    {detail.journeyMilestones.map((m, i) => (
                      <li key={i}>
                        <span className="drawer-milestone-time">T+{m.at_min}m</span>
                        <span className="drawer-milestone-event">{m.event}</span>
                      </li>
                    ))}
                  </ol>
                </section>
              )}

              {Object.keys(detail.rationales).length > 0 && (
                <section className="drawer-section">
                  <h3>Rationales</h3>
                  <dl className="drawer-rationales">
                    {Object.entries(detail.rationales).map(([axis, text]) => (
                      <div key={axis}>
                        <dt>{RATIONALE_LABELS[axis] ?? axis}</dt>
                        <dd>{text || <span className="muted">—</span>}</dd>
                      </div>
                    ))}
                  </dl>
                </section>
              )}
            </>
          )}
        </div>
      </aside>
    </>
  );
}
